import { ClientError } from '@ricoh/ria.client.core/errors';
import { omit } from '@ricoh/ria.client.core/utils/data/object';
import { camelCase } from 'change-case';

import type { BatchStage, BatchStatus, ProcessBatchType, ProcessStage } from '~/common/model/api/batch';

import { BATCH_STAGE_MAP_STATUS, BATCH_STATUSES, PROCESS_STAGE_MAP_STATUS } from '~/common/constants/feature/process/batch';

import type { ProcessBatch, TransformProcessBatch, TransformProcessBatchType } from './_batch.type';

/**
 * Prepare ProcessBatch Type.
 *
 * @param {TransformProcessBatchType} batchType ProcessBatch Type.
 *
 * @returns {ProcessBatchType} Prepared ProcessBatch Type.
 */
export function prepareProcessBatchType(batchType: TransformProcessBatchType): ProcessBatchType {
  return {
    ...batchType,
    isAIConfigured: batchType.isAIConfigured ?? false,
    documentTypes: batchType.documents
      ? Object.entries(batchType.documents).map(([documentTypeId, documentType]) => ({
          ...documentType,
          id: documentTypeId,
        }))
      : [],
  };
}

/**
 * Prepare Batch status.
 *
 * @param {string} batchStatus Status.
 * @param {ProcessStage} stage Stage.
 *
 * @returns {BatchStatus} Batch status.
 */
export function prepareBatchStatus(batchStatus: string, stage?: ProcessStage): BatchStatus {
  const status = camelCase(batchStatus);

  if (stage && !PROCESS_STAGE_MAP_STATUS[stage].includes(status)) {
    throw new ClientError({
      module: 'service',
      message: 'Not found batch in stage',
      cause: {
        code: 'InvalidBatchStatus',
        stage,
        batchStatus,
      },
    });
  }

  if (!BATCH_STATUSES.includes(status)) {
    throw new ClientError({
      module: 'service',
      message: 'Invalid batch status',
      cause: {
        code: 'InvalidBatchStatus',
        batchStatus,
      },
    });
  }

  return status;
}

/**
 * Prepare Batch Stage.
 *
 * @param {BatchStatus} batchStatus Status.
 *
 * @returns {BatchStage | undefined} Batch Stage.
 */
export function prepareBatchStage(batchStatus: BatchStatus): BatchStage | undefined {
  const stage = Object.entries<BatchStage, BatchStatus[]>(BATCH_STAGE_MAP_STATUS).find(([_, statuses]) => statuses.includes(batchStatus));
  if (!stage) {
    return undefined;
  }

  return stage[0];
}

/**
 * Transform ProcessBatch.
 *
 * @param {ProcessBatch} batch Process Batch.
 *
 * @returns {TransformProcessBatch} Transformed ProcessBatch.
 */
export function transformProcessBatch({ fields: batchFields, documents, ...batch }: ProcessBatch): TransformProcessBatch {
  return {
    ...batch,

    fields: Object.entries(batchFields).reduce(
      (fieldsPrev, [fieldId, { value }]) => ({
        ...fieldsPrev,
        [fieldId]: omit(value ?? {}, 'metadata'),
      }),
      {},
    ),

    documents: Object.entries(documents).reduce<TransformProcessBatch['documents']>((prev, [documentId, { fields: documentFields, ...document }]) => {
      prev[documentId] = {
        ...document,

        fields: documentFields
          ? Object.entries(documentFields).reduce(
              (fieldsPrev, [fieldId, { value }]) => ({
                ...fieldsPrev,
                [fieldId]: omit(value ?? {}, 'metadata'),
              }),
              {},
            )
          : undefined,
      };

      return prev;
    }, {}),
  };
}
